import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { userService } from '../../_services';
import { Redirect } from "react-router-dom";
import ColorJson from '../../../src/whitelabel.json';
import { cn, safeParseDate } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import FilterTagCom from '../FilterTag';

// New modern widget components
import BarListChart from '../BarListChart';
import ColumnPieChart from '../ColumnPieChart';
import ListChartWidget from '../ListChartWidget';
import MultiStackBarChart from '../MultiStackBarChart';

// Legacy components
import LineListChart from '../LineListChart';
import Claim from '../Claim';

function DistributorDashboard({ 
    dashboardName: propDashboardName, 
    startDate: propStartDate, 
    endDate: propEndDate, 
    handeChangefilter,
    handleAllDirectOverride_user,
    Wholesaler,
    DisEmail,
    DisName,
    dist_usertype: propDistUserType,
    filterUpdateTrigger
}) {
    // Calculate default dates
    const getDefaultDates = () => {
        const defaultStartDate = new Date();
        defaultStartDate.setDate(defaultStartDate.getDate() - 30);
        const defaultEndDate = new Date();
        return { defaultStartDate, defaultEndDate };
    };

    // Get stored dates from localStorage
    const getStoredDates = () => {
        const { defaultStartDate, defaultEndDate } = getDefaultDates();
        const storedStartDate = localStorage.getItem('User_Start_Date');
        const storedEndDate = localStorage.getItem('User_End_Date');
        
        const startdate = (storedStartDate && storedStartDate !== "" && storedStartDate !== "null")
            ? safeParseDate(storedStartDate, defaultStartDate)
            : defaultStartDate;
        const enddate = (storedEndDate && storedEndDate !== "" && storedEndDate !== "null")
            ? safeParseDate(storedEndDate, defaultEndDate)
            : defaultEndDate;
        
        return { startdate, enddate };
    };

    // Get user role info
    const getUserRoleInfo = () => {
        let roletype = '', rolename = '', whId = 0, distributorUserId = 0;
        try {
            const userdata = JSON.parse(localStorage.getItem('user'));
            try { distributorUserId = userdata.distributor_UserId; }
            catch (error) { }
            if (userdata.userrole === "Distributor" && userdata.wholesaler === "Avenel_lll") {
                roletype = "avdistributor";
                rolename = userdata.email;
                whId = userdata.wholesaler_Id;
            } else if (userdata.userrole === "Distributor" && userdata.wholesaler !== "Avenel_lll") {
                roletype = "distributor";
                rolename = userdata.paramsname;
                whId = userdata.wholesaler_Id;
            } else {
                roletype = userdata.userrole;
                rolename = userdata.paramsname;
                whId = userdata.wholesaler_Id;
            }
        } catch (error) { }
        return { roletype, rolename, whId, distributorUserId };
    };

    // Get initial filter data
    const getInitialFilterData = () => {
        let filterdatalocal = localStorage.getItem('Advance_Filter');
        if (filterdatalocal == null || filterdatalocal === '' || filterdatalocal === undefined || filterdatalocal === "null") {
            filterdatalocal = '';
        }
        return filterdatalocal;
    };

    // Initialize state
    const { startdate, enddate } = getStoredDates();
    const { roletype, rolename, whId, distributorUserId } = getUserRoleInfo();
    const userdata = JSON.parse(localStorage.getItem('user') || '{}');
    const isLegacyUser = userdata.isLegacyUser || false;

    const [state, setState] = useState({
        userdata: userdata,
        scrollArray: {},
        graphData: [],
        dashboardName: propDashboardName,
        wholesalerName: Wholesaler,
        DistributorEmail: DisEmail,
        IframeBaseURL: "",
        endDate: format(enddate, "yyyyMMdd"),
        startDate: format(startdate, "yyyyMMdd"),
        FilterTagMatch: [],
        FilterTagList: getInitialFilterData(),
        roletype: roletype,
        rolename: rolename,
        wh_Id: whId,
        distributor_UserId: distributorUserId,
        dist_usertype: propDistUserType
    });

    // Refs
    const prevDistUserTypeRef = useRef(propDistUserType);

    // Get colors based on domain
    const getColors = useCallback(() => {
        const host = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        const isRxHacker = host === "rxhacker.com" || 
                          host === "localhost:5464" || 
                          window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
                          window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
        
        if (isRxHacker) {
            return {
                primary: ColorJson.ApplicationColor.Rxhacker_primary_color,
                secondary: ColorJson.ApplicationColor.Rxhacker_secondary_color,
                extra: ColorJson.ApplicationColor.Rxhacker_extra_color
            };
        }
        return {
            primary: ColorJson.ApplicationColor.Glic_primary_color,
            secondary: ColorJson.ApplicationColor.Glic_secondary_color,
            extra: ColorJson.ApplicationColor.Glic_extra_color
        };
    }, []);

    // Build graph data
    const buildGraphData = useCallback(() => {
        let APIBaseURL = "";
        let wname = propDashboardName;
        const startDate = format(safeParseDate(propStartDate), "yyyyMMdd");
        const endDate = format(safeParseDate(propEndDate), "yyyyMMdd");
        const wholesalername = Wholesaler;
        const disemail = DisEmail;
        const AllDirectOverride = propDistUserType;

        if (wname == null || wname === '') {
            wname = DisName;
        }

        if (wholesalername === 'Avenel_llll') {
            wname = disemail;
            APIBaseURL = '/api/avds';
        } else {
            if (AllDirectOverride === 'All') {
                APIBaseURL = '/api/distsubdist_All';
            } else if (AllDirectOverride === 'Direct') {
                APIBaseURL = '/api/ds';
            } else if (AllDirectOverride === 'Override') {
                APIBaseURL = '/api/distsubdist';
            } else {
                APIBaseURL = '/api/ds';
            }
        }

        let AdFilterJson = '';
        try {
            AdFilterJson = JSON.parse(localStorage.getItem('Advance_Filter'));
        } catch (error) { }

        const filterTagList = (AdFilterJson != null && AdFilterJson !== '' && AdFilterJson !== undefined && AdFilterJson !== "null")
            ? JSON.stringify(AdFilterJson)
            : '';

        const { primary: primaryColor, secondary: secondaryColor, extra: extraColor } = getColors();
        const filterTime = localStorage.getItem('Filter_Last_Update_Time');
        const hdType = localStorage.getItem('Updated_HD_Type');

        // Request JSON builders
        const buildRequestJson = (chartType, rcount = 30, extra = {}) => {
            return JSON.stringify({
                wname: String(state.userdata?.distributor_UserId || ''),
                whname: String(state.userdata?.wholesaler_Id || ''),
                start_date: startDate,
                end_date: endDate,
                rcount: rcount,
                chart_type: chartType,
                filtertime: filterTime,
                rxsClaimType: hdType,
                ...extra
            });
        };

        const Column_PostData_RequestJson = buildRequestJson("columnList", 30, { ispatientsaving: "true", pharmacynumber: "all" });
        const MultiAxis_Column_PostData_RequestJson = buildRequestJson("column_multiaxislist", 0, { ispatientsaving: "true", pharmacynumber: "all" });
        const PostData_PatientSaving_True = buildRequestJson("lineList", 30, { ispatientsaving: "true", pharmacynumber: "all" });
        const PostData_PatientSaving_False = buildRequestJson("lineList", 30, { ispatientsaving: "false", pharmacynumber: "all" });
        const PostData_RequestJson = buildRequestJson("pielist", 30);
        const Drug_Transaction_PostData_RequestJson = buildRequestJson("list", 30);
        const PatientSavings_Pharmacy_RequestJson = buildRequestJson("list", 30);
        const Table_Pharmacy_RequestJson = buildRequestJson("list", 30);
        const TotalTransaction_PostData_RequestJson = buildRequestJson("columnList", 0, { ispatientsaving: "true", pharmacynumber: "all" });
        const Claim_PostData_RequestJson = buildRequestJson("list", 0);

        const baseWidgets = [
            {
                url: APIBaseURL + "/total_transactions_new",
                type: "Bar_List",
                title: "Total Transactions",
                Subtitle: "Total number of transactions for each day",
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "# of Transactions",
                interval: 'day',
                id: "DTotalTransaction",
                BorderRadius: 0,
                RequestJson: TotalTransaction_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/metric1a_new",
                type: "Bar_List",
                title: "Transactions by Group Number",
                Subtitle: "Number of transactions for each group number",
                ColorCode: secondaryColor,
                DataType: "GroupNumber",
                XlabelString: "Group Number",
                YlabelString: "# of Transactions",
                id: "DMetric1a",
                BorderRadius: 10,
                RequestJson: Column_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/patient_savings_new",
                type: "Line_List",
                title: "Average Patient Savings ($)",
                Subtitle: "Average savings for all patients in $",
                ColorCode: secondaryColor,
                XlabelString: "",
                YlabelString: "Average Saving",
                id: "DMetric3A",
                BorderRadius: 0,
                RequestJson: PostData_PatientSaving_True,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/patient_savings_new",
                type: "Line_List",
                title: "Average Patient Savings (%)",
                Subtitle: "Average savings for all patients in %",
                ColorCode: secondaryColor,
                XlabelString: "",
                YlabelString: "Average Saving",
                id: "DMetric3B",
                BorderRadius: 0,
                RequestJson: PostData_PatientSaving_False,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/statetransaction_new",
                type: "Column_Pie",
                title: "Transactions by State",
                Subtitle: "Number of transactions in each state",
                ColorCode: "#0282fb",
                DataType: "PhState",
                XlabelString: "States",
                YlabelString: "Number of Transactions",
                id: "DStateTransaction",
                BorderRadius: 0,
                RequestJson: PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/pharmacy_transaction_new",
                type: "List",
                title: "Transactions by Pharmacy",
                Subtitle: "Number of transactions at each pharmacy",
                ColorCode: "", DataType: "PharmacyName",
                XlabelString: "",
                YlabelString: "",
                id: "DPharmacyTransaction1",
                BorderRadius: 0,
                RequestJson: Table_Pharmacy_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/drugtransaction_new",
                type: "List",
                title: "Transactions by Drug",
                Subtitle: "Number of transactions for each drug",
                ColorCode: "", DataType: "DrugName",
                XlabelString: "",
                YlabelString: "",
                id: "DDrugTransaction",
                BorderRadius: 0,
                RequestJson: Drug_Transaction_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/commission_new",
                type: "Bar_List",
                title: "Total Commission Earned",
                Subtitle: "Commission earned for each month",
                ColorCode: secondaryColor,
                DataType: "NA",
                interval: 'month',
                XlabelString: "Date",
                YlabelString: "Commission",
                id: "DCommission",
                BorderRadius: 0,
                RequestJson: Column_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/commission_new1",
                type: "MultiBar_List",
                title: "Total Commission Earned",
                Subtitle: "Commission earned for each month",
                ColorCode: primaryColor,
                interval: 'month',
                XlabelString: "Date",
                YlabelString: "Commission",
                id: "DCommission1",
                BorderRadius: 0,
                RequestJson: MultiAxis_Column_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/wholesalertransaction_new",
                type: "List",
                title: "Transactions (Sub-Distributors)",
                Subtitle: "Transactions including sub-distributors",
                XlabelString: "",
                YlabelString: "",
                ColorCode: "",
                id: "DTransactions",
                BorderRadius: 0,
                RequestJson: Table_Pharmacy_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/prescribe_transaction_new",
                type: "List",
                title: "Transactions by Prescriber",
                  DataType: "PrescriberName",
                Subtitle: "Number of transactions at each Prescriber",
                ColorCode: extraColor,
                XlabelString: "",
                YlabelString: "",
                id: "WPrescriberTransaction1",
                RequestJson: Table_Pharmacy_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/patientsavings_new",
                type: "List",
                title: "Patient Savings by Pharmacy",
                  DataType: "PharmacyName",
                Subtitle: "Patient savings in $ at each pharmacy",
                ColorCode: "",
                XlabelString: "",
                YlabelString: "",
                id: "DPatientSavingTransaction",
                BorderRadius: 0,
                RequestJson: PatientSavings_Pharmacy_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/Repeat_Prescriptions_transaction_new",
                type: "List",
                title: "Transactions by Repeat Prescriptions",
                Subtitle: "Number of repeat prescriptions by each Prescriber",
                ColorCode: extraColor,
                DataType: "PrescriberName",
                XlabelString: "",
                YlabelString: "",
                id: "WRepeat_PrescriptionsTransaction",
                RequestJson: Table_Pharmacy_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            }
        ];

        // Add legacy user specific widgets
        if (isLegacyUser) {
            // Insert Claim widget after first widget for legacy users
            baseWidgets.splice(1, 0, {
                url: APIBaseURL + "/ClaimCalculation",
                type: "Claim",
                title: "Claim Counts",
                Subtitle: "",
                ColorCode: extraColor,
                id: "RxSensClaim",
                RequestJson: Claim_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            });
        }

        return { graphData: baseWidgets, filterTagList, startDate, endDate, dashboardName: wname };
    }, [propDashboardName, propStartDate, propEndDate, propDistUserType, Wholesaler, DisEmail, DisName, state.userdata, getColors]);

    // Change data handler
    const changeData = useCallback(() => {
        const result = buildGraphData();
        setState(prev => ({
            ...prev,
            graphData: result.graphData,
            FilterTagList: result.filterTagList,
            startDate: result.startDate,
            endDate: result.endDate,
            dashboardName: result.dashboardName
        }));
    }, [buildGraphData]);

    // Handle filter change
    const handlechangea = useCallback((e) => {
        if (e && e.heading && e.value) {
            // Validate heading is not undefined or invalid
            if (e.heading === 'undefined' || !e.heading) {
                return;
            }
            
            try {
                let Created_Advance_Filter = JSON.parse(localStorage.getItem('Advance_Filter')) || {};
                
                if (Created_Advance_Filter[e.heading]) {
                    if (!Created_Advance_Filter[e.heading].includes(e.value)) {
                        Created_Advance_Filter[e.heading].push(e.value);
                    }
                } else {
                    Created_Advance_Filter[e.heading] = [e.value];
                }
                
                localStorage.setItem('Advance_Filter', JSON.stringify(Created_Advance_Filter));
                localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
                localStorage.setItem('Updated_AdFilter_Status', JSON.stringify(Created_Advance_Filter));
                
            } catch (error) {
                console.error('Error saving filter:', error);
            }
        }
        
        if (handeChangefilter) {
            handeChangefilter(e);
        }
    }, [handeChangefilter]);

    // Initialize on mount
    useEffect(() => {
        localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
        changeData();
    }, []);

    // Handle dist_usertype changes
    useEffect(() => {
        if (prevDistUserTypeRef.current !== propDistUserType) {
            prevDistUserTypeRef.current = propDistUserType;
            setState(prev => ({ ...prev, dist_usertype: propDistUserType }));
            localStorage.setItem('dist_usertype', propDistUserType);
            changeData();
        }
    }, [propDistUserType, changeData]);

    // Check for filter/date changes and refresh data
    useEffect(() => {
        if (!propStartDate || !propEndDate) return;

        const startDate = format(safeParseDate(propStartDate), "yyyyMMdd");
        const endDate = format(safeParseDate(propEndDate), "yyyyMMdd");

        let IsCall = false;
        try {
            const prevFilter = localStorage.getItem('Prev_Filter_Status')?.toLowerCase();
            const updatedFilter = localStorage.getItem('Updated_Filter_Status')?.toLowerCase();
            const prevAdFilter = localStorage.getItem('Prev_AdFilter_Status');
            const updatedAdFilter = localStorage.getItem('Updated_AdFilter_Status');
            const prevHdType = localStorage.getItem('Prev_HD_Type');
            const updatedHdType = localStorage.getItem('Updated_HD_Type');

            if (prevFilter !== updatedFilter || prevAdFilter !== updatedAdFilter || prevHdType !== updatedHdType) {
                IsCall = true;
            }
        } catch (error) {
            IsCall = false;
        }

        if (endDate !== state.endDate || startDate !== state.startDate || IsCall) {
            if (IsCall) {
                localStorage.setItem('Prev_Filter_Status', localStorage.getItem('Updated_Filter_Status'));
                const updatedAdFilter = localStorage.getItem('Updated_AdFilter_Status');
                if (updatedAdFilter === "null" || updatedAdFilter === null) {
                    localStorage.setItem('Prev_AdFilter_Status', null);
                    localStorage.setItem('Updated_AdFilter_Status', null);
                } else {
                    localStorage.setItem('Prev_AdFilter_Status', updatedAdFilter);
                }
                const updatedHdType = localStorage.getItem('Updated_HD_Type');
                if (updatedHdType === "null" || updatedHdType === null) {
                    localStorage.setItem('Prev_HD_Type', null);
                    localStorage.setItem('Updated_HD_Type', null);
                } else {
                    localStorage.setItem('Prev_HD_Type', updatedHdType);
                }
            }
            changeData();
        }
    }, [propStartDate, propEndDate, state.startDate, state.endDate, changeData, filterUpdateTrigger]);

    // Check user permissions
    try {
        const usertype = userService.Get_User_Type();
        if (userService.Check_LoggedIn_Status() === 'Login') {
            return <Redirect to="/login" />;
        }
        if (usertype?.toLowerCase() === 'wholesaler') {
            return <Redirect to='/pageNotFound' />;
        } else if (usertype?.toLowerCase() === 'admin') {
            return <Redirect to='/pageNotFound' />;
        } else if (usertype?.toLowerCase() === 'subadmin') {
            return <Redirect to='/pageNotFound' />;
        }
    } catch (error) { }

    // Widget visibility helpers
    const fullWidthTypes = ['DTotalTransaction', 'RxSensClaim', 'RxSensClaimNew', 'DTransactions', 'DPharmacyTransaction1', 'WPrescriberTransaction1'];

    const isWidgetVisible = (item) => {
        const shouldHide = (
            (state.wholesalerName?.toLowerCase() === 'avenel_llll' && item.title === 'Total Commission Earned' && item.type === "MultiBar_List")
            || (state.wholesalerName?.toLowerCase() !== 'avenel_llll' && item.title === 'Total Commission Earned' && item.type === "Bar_List")
            || ((state.userdata?.wholesaler?.toLowerCase() === 'trustcare health, llc' && item.id === 'WPrescriberTransaction1') || (state.userdata?.wholesaler?.toLowerCase() === 'trustcare health, llc' && item.id === 'WRepeat_PrescriptionsTransaction'))
            || ((state.userdata?.wholesaler?.toLowerCase() === 'medresults, llc' && item.id === 'WPrescriberTransaction1') || (state.userdata?.wholesaler?.toLowerCase() === 'medresults, llc' && item.id === 'WRepeat_PrescriptionsTransaction'))
        );
        return !shouldHide;
    };

    // Render widget content based on type
    const renderWidgetContent = (item, index) => {
        switch (item.type) {
            // Modern widget components
            case "Bar_List":
                return <BarListChart data={item} key={"barlistgraph" + index} _Index={index} handlechangea={handlechangea} />;
            case "Column_Pie":
                return <ColumnPieChart data={item} key={"columnpiegraph" + index} _Index={index} handlechangea={handlechangea} />;
            case "List":
                return <ListChartWidget data={item} key={"listgraph" + index} _Index={index} handlechangea={handlechangea} />;
            case "MultiBar_List":
                return <MultiStackBarChart data={item} key={"barlistgraph1" + index} _Index={index} />;
            
            // Legacy components
            case "Line_List":
                return <LineListChart data={item} key={"linelistgraph" + index} _Index={index} />;
            case "Claim":
                return <Claim data={item} key={"claimbox" + index} _Index={index} />;
            default:
                return null;
        }
    };

    return (
        <div id="dashboard-container" className="space-y-6">
            {/* Filter Tags */}
            <FilterTagCom 
                dist_id={state.distributor_UserId} 
                Rolename={state.rolename} 
                wh_Id={state.wh_Id} 
                Roletype={state.roletype} 
                df={state.startDate} 
                dt={state.endDate} 
                handlechangea={handlechangea} 
                FilterTags={state.FilterTagList} 
            />

            {/* Dashboard Widgets Grid */}
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
                {state.graphData.length === 0 ? (
                    Array.from({ length: 12 }).map((_, i) => (
                        <div key={"skeleton-" + i} className="min-h-[420px]">
                            <Skeleton className="h-full w-full rounded-xl" />
                        </div>
                    ))
                ) : (
                    state.graphData.map((item, index) => {
                        if (!isWidgetVisible(item)) {
                            return null;
                        }

                        const isFullWidthItem = fullWidthTypes.includes(item.id);

                        return (
                            <div 
                                key={"graph-widget" + index}
                                id={item.id + index}
                                className={cn(
                                    item.type !== "Claim" && item.type !== "Claim_New" && "min-h-[420px]",
                                    isFullWidthItem && "2xl:col-span-2"
                                )}
                            >
                                {renderWidgetContent(item, index)}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default DistributorDashboard;
